import React, { useEffect, useRef, useState } from 'react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { ResponseTimeMarker } from '../../../../apm/response-time-measurement';
import { DashboardLayout } from '../../../../components/layout';
import { AreaToolbar, AreaToolbarButton } from '../../../../components/layout/area-toolbar';
import { StartPageTable } from '../../../../components/start-page-table/start-page-table';
import { useMetaTitle } from '../../../../hooks';
import { CourseFilterDto, CourseOverviewDto } from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { attendanceAreaPathName, getCourseEditPath, getCourseRedirectPath } from '../../attendance-area-pathnames';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { REFRESH_COMPANY_COURSE_TABLE } from '../course-uniform/course';
import { courseColumns } from './course-columns';
import { courseFilters } from './course-filters';
import { CourseLessonsPanel } from './course-lessons-panel';
import { useAttendanceRights } from '../../use-attendance-rights';

const attendanceClient = moduleClientFactory.createAttendanceClient();
const attendanceProtocolClient = moduleClientFactory.createAttendanceProtocolClient();

export function CoursesPage() {
  const { hasOnlyProtocolAccess, canCreateCourse } = useAttendanceRights();
  const [courses, setCourses] = useState<CourseOverviewDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState(hasOnlyProtocolAccess ? CourseFilterDto.Mine : CourseFilterDto.Active);
  const latestRequest = useRef(0);

  useMetaTitle('Arbejdsområde fremmøde - Hold');

  async function loadCourses() {
    latestRequest.current += 1;
    const request = latestRequest.current;
    // Only the answer to the latest filter may reach the table when filters are switched quickly
    const isLatest = () => request === latestRequest.current;
    try {
      setIsLoading(true);
      const result = hasOnlyProtocolAccess
        ? await attendanceProtocolClient.getCoursesForAttendanceProtocol()
        : await attendanceClient.getCourses({ filter });
      if (isLatest()) {
        setCourses(result.courses || []);
      }
    } catch (error) {
      // The client's error interceptor shows the error at the top of the page
    } finally {
      if (isLatest()) {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    loadCourses();
  }, [filter]);

  usePostToTab(REFRESH_COMPANY_COURSE_TABLE, () => loadCourses());

  function handleCreateClick() {
    window.open(`/${attendanceAreaPathName}/${getCourseRedirectPath()}`);
  }

  function handleRowClick(row: CourseOverviewDto) {
    window.open(`/${attendanceAreaPathName}/${getCourseEditPath(row.courseId)}`);
  }

  return (
    <DashboardLayout
      toolbar={
        <AreaToolbar
          mainItemGuid="43f675c0-9629-4ee2-be8f-d02012693834"
          prefixedText="Arbejdsområde fremmøde - "
          tools={
            canCreateCourse
              ? [
                  <AreaToolbarButton key="add" icon={faPlus} color="primary" variant="text" iconPos="left" onClick={handleCreateClick}>
                    Opret nyt hold
                  </AreaToolbarButton>,
                ]
              : []
          }
        />
      }
    >
      <StartPageTable<CourseOverviewDto>
        title="Hold"
        context="Courses"
        universe="company"
        columns={courseColumns}
        rows={courses}
        getRowId={row => row.courseId}
        getRootEntityId={hasOnlyProtocolAccess ? undefined : row => row.companyId}
        isLoading={isLoading}
        onRowClick={hasOnlyProtocolAccess ? undefined : handleRowClick}
        onRefresh={loadCourses}
        filters={hasOnlyProtocolAccess ? courseFilters.filter(courseFilter => courseFilter.id === CourseFilterDto.Mine) : courseFilters}
        selectedFilterId={filter}
        onFilterChange={filterId => setFilter(filterId as CourseFilterDto)}
        getDetailPanelContent={({ row }) => (row.hasCurrentLessons ? <CourseLessonsPanel courseId={row.courseId} /> : null)}
      />
      <ResponseTimeMarker measurement="FM_vishold" isReady={!isLoading} />
    </DashboardLayout>
  );
}
